import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IError } from '@axmit/error-helper';
import { AppDispatch } from 'app/store';
import {
  IStockReconciliationShiftCollection,
  IStockReconciliationShiftFilters,
  IStockReconciliationShiftItemsCollection,
  IStockReconciliationShiftItemsFilters,
} from 'entities/StockReconciliation/StockReconciliation.models';
import { stockReconciliationTransport } from 'entities/StockReconciliation/StockReconciliation.transport';

export interface IStockReconciliationState {
  stockReconciliationShiftCollectionLoading: boolean;
  stockReconciliationShiftCollection: IStockReconciliationShiftCollection | null;
  stockReconciliationShiftCollectionError: IError | null;

  // Per-shift items state — expanded rows render independently; shared slice would collide
  shiftItemsByShiftKey: Record<string, IStockReconciliationShiftItemsCollection | undefined>;
  isLoadingByShiftKey: Record<string, boolean | undefined>;
  errorByShiftKey: Record<string, IError | null | undefined>;
}

const initialState: IStockReconciliationState = {
  stockReconciliationShiftCollectionLoading: false,
  stockReconciliationShiftCollection: null,
  stockReconciliationShiftCollectionError: null,

  shiftItemsByShiftKey: {},
  isLoadingByShiftKey: {},
  errorByShiftKey: {},
};

export const stockReconciliationSlice = createSlice({
  name: 'stockReconciliation',
  initialState,
  reducers: {
    setStockReconciliationShiftCollectionLoading(state, action: PayloadAction<boolean>) {
      state.stockReconciliationShiftCollectionLoading = action.payload;
    },
    setStockReconciliationShiftCollection(state, action: PayloadAction<IStockReconciliationShiftCollection | null>) {
      state.stockReconciliationShiftCollection = action.payload;
      // Clear stale per-shift items when the shift list changes (week/store/uom switch)
      state.shiftItemsByShiftKey = {};
      state.isLoadingByShiftKey = {};
      state.errorByShiftKey = {};
    },
    setStockReconciliationShiftCollectionError(state, action: PayloadAction<IError | null>) {
      state.stockReconciliationShiftCollectionError = action.payload;
    },
    setStockReconciliationShiftItemsLoading(
      state,
      action: PayloadAction<{ shiftKey: string; loading: boolean }>
    ) {
      state.isLoadingByShiftKey[action.payload.shiftKey] = action.payload.loading;
    },
    setStockReconciliationShiftItems(
      state,
      action: PayloadAction<{ shiftKey: string; collection: IStockReconciliationShiftItemsCollection | null }>
    ) {
      state.shiftItemsByShiftKey[action.payload.shiftKey] = action.payload.collection ?? undefined;
    },
    setStockReconciliationShiftItemsError(
      state,
      action: PayloadAction<{ shiftKey: string; error: IError | null }>
    ) {
      state.errorByShiftKey[action.payload.shiftKey] = action.payload.error;
    },
  },
});

export const {
  setStockReconciliationShiftCollectionLoading,
  setStockReconciliationShiftCollection,
  setStockReconciliationShiftCollectionError,
  setStockReconciliationShiftItemsLoading,
  setStockReconciliationShiftItems,
  setStockReconciliationShiftItemsError,
} = stockReconciliationSlice.actions;
export default stockReconciliationSlice.reducer;

export const getStockReconciliationShiftCollection = (filters: IStockReconciliationShiftFilters) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setStockReconciliationShiftCollectionLoading(true));
    try {
      const result = await stockReconciliationTransport.getShiftCollection(filters);
      dispatch(setStockReconciliationShiftCollection(result));
    } catch (error) {
      dispatch(setStockReconciliationShiftCollectionError(error as IError));
    } finally {
      dispatch(setStockReconciliationShiftCollectionLoading(false));
    }
  }
  return thunk;
};

export const getStockReconciliationShiftItemsCollection = (filters: IStockReconciliationShiftItemsFilters) => {
  async function thunk(dispatch: AppDispatch) {
    const { shiftKey } = filters;
    dispatch(setStockReconciliationShiftItemsLoading({ shiftKey, loading: true }));
    try {
      const result = await stockReconciliationTransport.getShiftItemsCollection(filters);
      dispatch(setStockReconciliationShiftItems({ shiftKey, collection: result }));
      dispatch(setStockReconciliationShiftItemsError({ shiftKey, error: null }));
    } catch (error) {
      dispatch(setStockReconciliationShiftItemsError({ shiftKey, error: error as IError }));
    } finally {
      dispatch(setStockReconciliationShiftItemsLoading({ shiftKey, loading: false }));
    }
  }
  return thunk;
};
